import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FieldArray } from 'react-final-form-arrays';
import { injectIntl, intlShape } from '../../util/reactIntl';
import css from './ListingFieldReturnCareInstructions.module.css';
import IconCollection from '../IconCollection/IconCollection';
import Button from '../Button/Button';
import Modal from '../Modal/Modal';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ListingGroupCheckboxFieldCss from '../ListingFieldGroupCheckbox/ListingGroupCheckboxField.module.css';
import Chevron from '../Chevron/Chevron';
import FieldCheckbox from '../FieldCheckbox/FieldCheckbox';
import classNames from 'classnames';
import ToolTip from '../ToolTip/ToolTip';

function ReturnCareModal({ isOpen, onClose, onSubmit, initialValues, intl, editIdx }) {
    const [instruction, setInstruction] = useState(initialValues || '');
    const [touched, setTouched] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setInstruction(initialValues || '');
        setTouched(false);
        setSubmitting(false);
    }, [initialValues, isOpen]);

    const handleChange = value => {
        setInstruction(value);
        setTouched(true);
    };

    const handleConfirm = () => {
        setSubmitting(true);
        if (!instruction || instruction.trim() === '') {
            setTouched(true);
            setSubmitting(false);
            return;
        }
        onSubmit(instruction);
        setSubmitting(false);
    };

    return (
        <Modal id="return-care-modal" isOpen={isOpen} onClose={onClose} onManageDisableScrolling={() => { }}>
            <div className={css.modalContent}>
                <h3>{intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.modalTitle', defaultMessage: 'Return Care Instruction' })}</h3>
                <div className={css.modalFieldsGrid}>
                    <div className={css.modalFieldRow}>
                        <label>{intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.instructionLabel', defaultMessage: 'Write an Instruction' })}</label>
                        <input
                            type="text"
                            value={instruction || ''}
                            onChange={e => handleChange(e.target.value)}
                            placeholder={intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.instructionPlaceholder', defaultMessage: 'e.g. Ensure the item is clean' })}
                            maxLength={120}
                        />
                        <span className={css.helperText}>{(instruction || '').length}/120</span>
                        {touched && !instruction && <span className={css.error}>{intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.required', defaultMessage: 'Required' })}</span>}
                    </div>
                </div>
                <div className={css.modalActions}>
                    <Button variant="info" type="button" onClick={onClose} className={css.cancelBtn}>
                        {intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Button variant="primary" type="button" onClick={handleConfirm} className={css.confirmBtn} disabled={submitting}>
                        {editIdx !== null ? intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.update', defaultMessage: 'Update' }) : intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.confirm', defaultMessage: 'Confirm' })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

const ListingFieldReturnCareInstructions = ({ name, intl, onRegenerate, regenerateDisabled, showRegenerate = true, generateInProgress, creditsUsed, maxCredits, onCreditsInfoClick, showCredits = true, showChevron = true, showToggle = true, toggleValue, enabled }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [editIdx, setEditIdx] = useState(null);
    const [editInitial, setEditInitial] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 767);

    useEffect(() => {
        const handleResize = () => {
            if (typeof window !== 'undefined') {
                setIsMobile(window.innerWidth <= 767);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const hiddenClass = !expanded ? css.hidden : css.body;

    return (
        <FieldArray name={name}>
            {({ fields }) => (
                <div className={css.root}>
                    <div className={`${ListingGroupCheckboxFieldCss.sectionHeader} ${css.headerRow}`}>
                        <div>
                            <span className={css.headerTitle}>{intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.title', defaultMessage: 'Return Care Instructions' })}</span>
                        </div>
                        <div className={ListingGroupCheckboxFieldCss.sectionControls}>
                            {!isMobile && showRegenerate && (
                                <Button
                                    variant="warning"
                                    type="button"
                                    size={"sm"} className={classNames(ListingGroupCheckboxFieldCss.regenerateBtn, hiddenClass)}
                                    onClick={onRegenerate}
                                    disabled={regenerateDisabled}
                                >
                                    <span className={classNames(ListingGroupCheckboxFieldCss.regenerateIcon, generateInProgress && ListingGroupCheckboxFieldCss.spinner)}>
                                        <IconCollection name="icon_regenrate" width={16} height={16} />
                                    </span> Regenerate
                                </Button>
                            )}
                            {!isMobile && (
                                <Button size={"sm"} variant="danger" type="button" className={classNames(css.addBtn, hiddenClass)} onClick={() => { setEditIdx(null); setEditInitial(null); setModalOpen(true); }}>
                                    <IconCollection name="plus_icon" width={18} height={18} /> {intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.addInstruction', defaultMessage: 'Add Instruction' })}
                                </Button>
                            )}
                            {showToggle && (
                                <div className={ListingGroupCheckboxFieldCss.toggleWrapper}>
                                    <label className={ListingGroupCheckboxFieldCss.toggleLabel}>
                                        <FieldCheckbox
                                            id={`${name}Enabled`}
                                            name={`${name}Enabled`}
                                            value={toggleValue}
                                            label=""
                                            className={ListingGroupCheckboxFieldCss.toggleInput}
                                        />
                                        <span className={classNames(ListingGroupCheckboxFieldCss.toggleSlider, { [ListingGroupCheckboxFieldCss.enabled]: enabled })}></span>
                                    </label>
                                    <span className={ListingGroupCheckboxFieldCss.toggleLabelText}>Enabled</span>
                                </div>
                            )}
                            {showChevron && (
                                <button
                                    type="button"
                                    className={ListingGroupCheckboxFieldCss.toggleBtn}
                                    onClick={() => setExpanded(e => !e)}
                                    aria-label={expanded ? 'Collapse' : 'Expand'}
                                >
                                    <Chevron expanded={expanded} width={24} height={24} />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className={css.label}>{intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.helperText', defaultMessage: 'Guide renters on how to care for and return your item properly.' })}</div>
                    <div className={hiddenClass}>
                        {isMobile && <Button size={"sm"} variant="danger" type="button" className={css.addBtn} onClick={() => { setEditIdx(null); setEditInitial(null); setModalOpen(true); }}>
                            <IconCollection name="plus_icon" width={18} height={18} /> {intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.addInstruction', defaultMessage: 'Add Instruction' })}
                        </Button>}
                        {fields.length === 0 ? (
                            <div className={css.emptyState}>
                                {intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.empty', defaultMessage: 'No instructions added yet.' })}
                            </div>
                        ) : (
                            <DragDropContext
                                onDragEnd={result => {
                                    if (!result.destination) return;
                                    if (result.destination.index === result.source.index) return;
                                    const newOrder = reorder(fields.value, result.source.index, result.destination.index);
                                    newOrder.forEach((item, idx) => fields.update(idx, item));
                                }}
                            >
                                <Droppable droppableId="instruction-list">
                                    {(provided) => (
                                        <div className={css.instructionList} ref={provided.innerRef} {...provided.droppableProps}>
                                            {fields.map((name, idx) => {
                                                const instruction = fields.value[idx];
                                                return (
                                                    <Draggable key={name} draggableId={name} index={idx}>
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                className={css.instructionItem + (snapshot.isDragging ? ' ' + css.instructionItemDragging : '')}
                                                            >
                                                                <div className={css.instructionMainRow}>
                                                                    <div className={css.dragHandle} {...provided.dragHandleProps}>
                                                                        <IconCollection name="icon_drag" width={18} height={18} />
                                                                    </div>
                                                                    <div className={css.instructionLabel}>{instruction || <span className={css.placeholder}>{intl.formatMessage({ id: 'ListingFieldReturnCareInstructions.noInstruction', defaultMessage: 'No instruction' })}</span>}</div>
                                                                    <div className={css.instructionActions}>
                                                                        <Button type="button" className={css.iconBtn} onClick={() => { setEditIdx(idx); setEditInitial(instruction); setModalOpen(true); }}>
                                                                            <IconCollection name="icon_pencil" width={18} height={18} />
                                                                        </Button>
                                                                        <Button type="button" className={css.iconBtn} onClick={() => fields.remove(idx)}>
                                                                            <IconCollection name="icon_trash" width={18} height={18} />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        )}
                    </div>
                    <div className={classNames(css.mobileActions, hiddenClass)}>
                        {isMobile && showRegenerate && (
                            <Button
                                variant="warning"
                                type="button"
                                size={"sm"} className={ListingGroupCheckboxFieldCss.regenerateBtn}
                                onClick={onRegenerate}
                                disabled={regenerateDisabled}
                            >
                                <span className={classNames(ListingGroupCheckboxFieldCss.regenerateIcon, generateInProgress && ListingGroupCheckboxFieldCss.spinner)}>
                                    <IconCollection name="icon_regenrate" width={16} height={16} />
                                </span> Regenerate
                            </Button>
                        )}
                        {showCredits && (
                            <div className={classNames(ListingGroupCheckboxFieldCss.creditsRow, hiddenClass)}>
                                <span className={ListingGroupCheckboxFieldCss.creditsText}>{creditsUsed}/{maxCredits} credits used</span>
                                {/* {typeof onCreditsInfoClick === 'function' && (
                                    <button type="button" className={ListingGroupCheckboxFieldCss.creditsInfoBtn} onClick={onCreditsInfoClick}>
                                        <IconCollection name="info_icon" width={18} height={18} />
                                    </button>
                                )} */}
                                <ToolTip content={`${creditsUsed}/${maxCredits} credits used`}>
                                    <button type="button" className={ListingGroupCheckboxFieldCss.creditsInfoBtn} >
                                        <IconCollection name="info_icon" width={16} height={16} />
                                    </button>
                                </ToolTip>
                            </div>
                        )}
                    </div>
                    <ReturnCareModal
                        isOpen={modalOpen}
                        onClose={() => setModalOpen(false)}
                        initialValues={fields?.value?.[editIdx] || ''}
                        onSubmit={instruction => {
                            console.log(instruction);
                            if (editIdx !== null) {
                                fields.update(editIdx, instruction);
                            } else {
                                fields.push(instruction);
                            }
                            setModalOpen(false);
                        }}
                        intl={intl}
                        editIdx={editIdx}
                    />
                </div>
            )}
        </FieldArray>
    );
};

ListingFieldReturnCareInstructions.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.array,
    intl: intlShape.isRequired,
    showChevron: PropTypes.bool,
    showToggle: PropTypes.bool,
    toggleValue: PropTypes.bool,
    enabled: PropTypes.bool,
};

export default injectIntl(ListingFieldReturnCareInstructions);
